import React, { useEffect, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import axios from 'axios';
import { Check, Loader2, ShoppingBag } from 'lucide-react';
import StoreNav from '../components/store/StoreNav';
import PublicSiteFooter from '../components/store/PublicSiteFooter';
import ProductGallery from '../components/store/ProductGallery';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { useAuth } from '../context/AuthContext';
import { defaultPricingRegion, formatProductPrice } from '../lib/productPricing';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export default function TemplateDetailPage() {
  const { slug } = useParams();
  const navigate = useNavigate();
  const { user, api } = useAuth();
  const [product, setProduct] = useState(null);
  const [loading, setLoading] = useState(true);
  const [purchasing, setPurchasing] = useState(false);
  const pricingRegion = defaultPricingRegion(user);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      setLoading(true);
      try {
        const client = user ? api : axios.create({ baseURL: `${API_URL}/api` });
        const res = await client.get(`/products/${slug}`);
        if (!cancelled) setProduct(res.data);
      } catch {
        if (!cancelled) setProduct(null);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [api, slug, user]);

  const handleBuy = async () => {
    if (!user) {
      navigate(`/login?redirect=${encodeURIComponent(`/templates/${slug}`)}`);
      return;
    }
    if (product?.already_purchased) {
      navigate('/my-library');
      return;
    }

    setPurchasing(true);
    try {
      const callbackUrl = `${window.location.origin}/payment/callback?type=product`;
      const res = await api.post(`/products/${slug}/purchase/initialize`, { callback_url: callbackUrl });
      if (res.data.mock) {
        const verify = await api.get(`/payments/flutterwave/product/verify/${res.data.reference}`);
        if (verify.data.status === 'success') {
          toast.success('Purchase complete! Your product is in My Library.');
          navigate('/my-library?purchased=1');
        } else {
          toast.error(verify.data.message || 'Payment could not be confirmed.');
        }
      } else if (res.data.checkout_url) {
        window.location.href = res.data.checkout_url;
      } else {
        throw new Error('Could not start checkout.');
      }
    } catch (error) {
      const detail = error.response?.data?.detail;
      toast.error(typeof detail === 'string' ? detail : error.message || 'Checkout failed');
    } finally {
      setPurchasing(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-muted/20">
        <StoreNav />
        <div className="flex min-h-[50vh] items-center justify-center">
          <Loader2 className="h-8 w-8 animate-spin text-primary" />
        </div>
        <PublicSiteFooter />
      </div>
    );
  }

  if (!product) {
    return (
      <div className="min-h-screen bg-muted/20">
        <StoreNav />
        <div className="mx-auto max-w-3xl px-6 py-16 text-center">
          <h1 className="text-2xl font-bold">Product not found</h1>
          <p className="mt-2 text-muted-foreground">This template or toolkit is unavailable.</p>
          <Button asChild className="mt-6"><Link to="/templates">Back to store</Link></Button>
        </div>
        <PublicSiteFooter />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-muted/20">
      <StoreNav />
      <main className="mx-auto max-w-[1200px] px-6 py-10">
        <div className="mb-6">
          <Link to="/templates" className="text-sm text-muted-foreground hover:text-foreground">← Back to Templates &amp; Toolkits</Link>
        </div>

        <div className="grid gap-10 lg:grid-cols-2">
          <ProductGallery gallery={product.gallery} />

          <div className="space-y-6">
            <div>
              <p className="text-sm font-medium uppercase tracking-wide text-muted-foreground capitalize">{product.category}</p>
              <h1 className="mt-1 text-3xl font-bold text-foreground">{product.name}</h1>
              <p className="mt-3 text-muted-foreground">{product.description}</p>
            </div>

            <Card>
              <CardContent className="space-y-4 p-6">
                <p className="text-3xl font-bold text-primary">{formatProductPrice(product, pricingRegion)}</p>
                {product.already_purchased ? (
                  <Button className="w-full" size="lg" onClick={() => navigate('/my-library')} data-testid="go-to-library-btn">
                    Go to My Library
                  </Button>
                ) : (
                  <Button className="w-full glow-button" size="lg" onClick={handleBuy} disabled={purchasing} data-testid="buy-now-btn">
                    {purchasing ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <ShoppingBag className="mr-2 h-4 w-4" />}
                    Buy Now
                  </Button>
                )}
              </CardContent>
            </Card>

            {(product.features || []).length > 0 && (
              <div>
                <h2 className="text-xl font-semibold mb-3">What&apos;s Included</h2>
                <ul className="space-y-2">
                  {product.features.map((feature) => (
                    <li key={feature} className="flex gap-2 text-sm text-foreground/90">
                      <Check className="mt-0.5 h-4 w-4 shrink-0 text-primary" />
                      <span>{feature}</span>
                    </li>
                  ))}
                </ul>
              </div>
            )}
          </div>
        </div>
      </main>
      <PublicSiteFooter />
    </div>
  );
}
